import { apiBase } from '../native/platform';

/**
 * Splits an event-stream buffer into complete events and the unfinished tail. Comments
 * (lines starting with ":") and unknown fields are dropped; a `data:` line must hold JSON
 * with a numeric `seq` to count.
 */
export function parseEvents(buffer: string): { seqs: number[]; rest: string } {
  const seqs: number[] = [];
  let rest = buffer.replace(/\r\n/g, '\n');
  for (;;) {
    const end = rest.indexOf('\n\n');
    if (end === -1) break;
    const block = rest.slice(0, end);
    rest = rest.slice(end + 2);
    for (const line of block.split('\n')) {
      if (!line.startsWith('data:')) continue;
      try {
        const parsed = JSON.parse(line.slice(5).trim()) as { seq?: unknown };
        if (typeof parsed.seq === 'number') seqs.push(parsed.seq);
      } catch {
        // not ours; the next pull would show anything real
      }
    }
  }
  return { seqs, rest };
}

export interface LiveHandlers {
  token(): string | null;
  /** The server's sequence moved (or the stream just opened and reports where it stands). */
  wake(seq: number): void;
  onState(live: boolean): void;
  onUnauthorized(): void;
}

const BACKOFF_MIN = 1000;
const BACKOFF_MAX = 60_000;
/** A stream that lived this long was cut by something on the way, not by a failure: reconnect at once. */
const HEALTHY_MS = 30_000;
/** Pings come every 25 s; silence for this long means a dead connection, such as after a network switch. */
const WATCHDOG_MS = 60_000;
/** An older server without the stream: ask again later rather than hammer it. */
const RETRY_MISSING_MS = 60 * 60_000;

let handlers: LiveHandlers | null = null;
let wanted = false;
let active = true;
let controller: AbortController | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let backoff = BACKOFF_MIN;

function foreground(): boolean {
  return active && (typeof document === 'undefined' || document.visibilityState !== 'hidden');
}

function online(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Opens the stream whenever the device should be listening: signed in, in front, online. */
export function startLive(h: LiveHandlers): void {
  handlers = h;
  wanted = true;
  backoff = BACKOFF_MIN;
  connect();
}

export function stopLive(): void {
  wanted = false;
  disconnect();
  handlers = null;
}

/** The Android shell reports foreground and background; a hidden tab is handled through visibilitychange. */
export function setLiveActive(isActive: boolean): void {
  active = isActive;
  if (isActive) resumeLive();
  else pauseLive();
}

export function pauseLive(): void {
  disconnect();
}

export function resumeLive(): void {
  if (!wanted) return;
  backoff = BACKOFF_MIN;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  connect();
}

export function liveConnected(): boolean {
  return controller !== null;
}

function disconnect(): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  const c = controller;
  controller = null;
  c?.abort();
  handlers?.onState(false);
}

function later(delay: number): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    connect();
  }, delay);
}

function connect(): void {
  if (!wanted || controller || retryTimer || !handlers) return;
  const token = handlers.token();
  if (!token || !foreground() || !online()) return;
  const ac = new AbortController();
  controller = ac;
  const opened = Date.now();
  run(ac, token)
    .catch((e: unknown) => (e instanceof Error && e.name === 'AbortError' ? ('aborted' as const) : e instanceof Error ? e.message : String(e)))
    .then((outcome) => {
      if (controller !== ac) return;
      controller = null;
      handlers?.onState(false);
      if (outcome === 'aborted' || !wanted || !foreground() || !online()) return;
      if (outcome === 'unauthorized') {
        handlers?.onUnauthorized();
        return;
      }
      if (outcome === 'missing') {
        later(RETRY_MISSING_MS);
        return;
      }
      if (Date.now() - opened > HEALTHY_MS) {
        backoff = BACKOFF_MIN;
        later(0);
        return;
      }
      const delay = backoff + Math.random() * 500;
      backoff = Math.min(backoff * 2, BACKOFF_MAX);
      later(delay);
    });
}

/** Resolves 'ended' when the server closed the stream, 'timeout' when the watchdog gave up on a silent one. */
async function run(ac: AbortController, token: string): Promise<'ended' | 'timeout'> {
  const res = await fetch(`${apiBase()}/api/sync/events`, {
    headers: { authorization: `Bearer ${token}`, accept: 'text/event-stream' },
    cache: 'no-store',
    credentials: 'omit',
    signal: ac.signal,
  });
  if (res.status === 401) throw new Error('unauthorized');
  if (res.status === 404) throw new Error('missing');
  if (!res.ok || !res.body) throw new Error(`events: HTTP ${res.status}`);
  handlers?.onState(true);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let timedOut = false;
  const bark = () => {
    timedOut = true;
    ac.abort();
  };
  let watchdog = setTimeout(bark, WATCHDOG_MS);
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return 'ended';
      clearTimeout(watchdog);
      watchdog = setTimeout(bark, WATCHDOG_MS);
      buffer += decoder.decode(value, { stream: true });
      const { seqs, rest } = parseEvents(buffer);
      buffer = rest;
      for (const seq of seqs) handlers?.wake(seq);
    }
  } catch (e) {
    if (timedOut) return 'timeout';
    throw e;
  } finally {
    clearTimeout(watchdog);
  }
}
